import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, Car, Filter, X, ChevronLeft, ChevronRight } from 'lucide-react'
import { ChevronDown, ChevronRight as ChevronRightFilter } from 'lucide-react';
import { API_ENDPOINTS, fetchFromAPI } from '../constants/api';
import CarComponent from '../components/CarComponent'
import { useTranslation } from '../hooks/useTranslation';
import { useCurrency } from '../contexts/CurrencyContext';

export default function CarsClient({ initialCars, cFilters, homeData, initialSearchParams, popularCarsData }) {
  //console.log("cfilters ===========> ", cFilters);
  //console.log("initialSearchParams =========>", initialSearchParams);
  //console.log("homeData ===============>", homeData);
  const { selectedCurrency } = useCurrency();
  const { t } = useTranslation();
  const router = useNavigate();
  const location = useLocation();

  const [allCars, setAllCars] = useState(initialCars);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState(initialSearchParams?.search || '')
  const [sortBy, setSortBy] = useState(initialSearchParams?.sort_by || 'name')
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Slider state for popular cars
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slidesToShow, setSlidesToShow] = useState(4);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(parseInt(initialSearchParams?.page) || 1);
  const [totalCars, setTotalCars] = useState(homeData.total);
  const [totalPages, setTotalPages] = useState(homeData.last_page);
  const itemsPerPage = 20;

  // Collapsible filter states
  const [showBodyType, setShowBodyType] = useState(false)
  const [showTransmission, setShowTransmission] = useState(false)
  const [showBrands, setShowBrands] = useState(false)
  const [showColor, setShowColor] = useState(false)
  const [showFuel, setShowFuel] = useState(false)

  // Selected cFilters - initialize from URL params
  const [selectedFilters, setSelectedFilters] = useState({
    bodyTypes: initialSearchParams?.body_type_ids || [],
    transmission: initialSearchParams?.transmission || '',
    brands: initialSearchParams?.brand_ids || [],
    colors: initialSearchParams?.color_ids || [],
    fuelType: initialSearchParams?.fuel_type || ''
  });

  // Update selectedFilters when initialSearchParams change (only on initial load)
  useEffect(() => {
    if (selectedFilters.brands.length > 0) setShowBrands(true);
    if (selectedFilters.bodyTypes.length > 0) setShowBodyType(true);
    if (selectedFilters.colors.length > 0) setShowColor(true);
    if (selectedFilters.fuelType != '') setShowFuel(true);
    if (selectedFilters.transmission != '') setShowTransmission(true);
  }, []);


  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);

    const toIntOrKeep = (value) => {
      if (value === null || value === undefined) return value;
      const num = Number(value);
      return Number.isInteger(num) ? num : value;
    };

    const parseArrayParam = (paramName) => {
      return urlParams.getAll(paramName).map(v => toIntOrKeep(v));
    };

    const parseSingleParam = (paramName) => {
      const value = urlParams.get(paramName);
      return toIntOrKeep(value);
    };

    const newFilters = {
      bodyTypes: parseArrayParam('body_type_ids[]'),
      transmission: parseSingleParam('transmission'),
      brands: parseArrayParam('brand_ids[]'),
      colors: parseArrayParam('color_ids[]'),
      fuelType: parseSingleParam('fuel_type')
    };

    const page = toIntOrKeep(urlParams.get('page')) || 1;
    const search = urlParams.get('search') || '';
    const sort = urlParams.get('sort_by') || 'name';

    setSelectedFilters(newFilters);
    setCurrentPage(page);
    setSearchTerm(search);
    setSortBy(sort);

    if (newFilters.brands.length) setShowBrands(true);
    if (newFilters.bodyTypes.length) setShowBodyType(true);
    if (newFilters.colors.length) setShowColor(true);
    if (newFilters.fuelType) setShowFuel(true);
    if (newFilters.transmission) setShowTransmission(true);

  }, [location.search]);


  // Handle responsive slides to show
  useEffect(() => {
    const updateSlidesToShow = () => {
      if (window.innerWidth >= 1280) { // xl
        setSlidesToShow(3);
      } else if (window.innerWidth >= 1024) { // lg
        setSlidesToShow(3);
      } else if (window.innerWidth >= 640) { // sm
        setSlidesToShow(2);
      } else {
        setSlidesToShow(1);
      }
    };

    updateSlidesToShow();
    window.addEventListener('resize', updateSlidesToShow);
    return () => window.removeEventListener('resize', updateSlidesToShow);
  }, []);

  // Auto-play slider
  useEffect(() => {
    if (!popularCarsData || popularCarsData.length <= slidesToShow) return;

    // Detect mobile (you can adjust px breakpoint)
    const isMobile = window.innerWidth < 640; // e.g. Tailwind 'sm' breakpoint

    if (isMobile) return; // ❌ stop slider on mobile

    const interval = setInterval(() => {
      setCurrentSlide(prev => {
        const maxSlide = popularCarsData.length - slidesToShow;
        return prev >= maxSlide ? 0 : prev + 1;
      });
    }, 4000);

    return () => clearInterval(interval);
  }, [popularCarsData, slidesToShow]);


  // Slider navigation functions
  const nextSlide = () => {
    if (!popularCarsData || popularCarsData.length <= slidesToShow) return;
    setCurrentSlide(prev => {
      const maxSlide = popularCarsData.length - slidesToShow;
      return prev >= maxSlide ? 0 : prev + 1;
    });
  };

  const prevSlide = () => {
    if (!popularCarsData || popularCarsData.length <= slidesToShow) return;
    setCurrentSlide(prev => {
      const maxSlide = popularCarsData.length - slidesToShow;
      return prev <= 0 ? maxSlide : prev - 1;
    });
  };

  // Debounced search function
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  // Update URL with new cFilters and pagination
  const updateURL = useCallback((newFilters, newSearchTerm, newSortBy, page = 1) => {
    const params = new URLSearchParams();

    // Add search term
    if (newSearchTerm) {
      params.set('search', newSearchTerm);
    }

    // Add sort
    if (newSortBy && newSortBy !== 'name') {
      params.set('sort_by', newSortBy);
    }

    // Add brand cFilters (multiple values)
    if (newFilters.brands?.length > 0) {
      newFilters.brands.forEach(brand => {
        params.append('brand_ids[]', brand);
      });
    }

    // Add fuel type filter (single value)
    if (newFilters.fuelType !== null) {
      params.set('fuel_type', newFilters.fuelType);
    }

    // Add transmission filter (single value)
    if (newFilters.transmission !== null) {
      params.set('transmission', newFilters.transmission);
    }

    // Add body type cFilters (multiple values)
    if (newFilters.bodyTypes?.length > 0) {
      newFilters.bodyTypes.forEach(bodyType => {
        params.append('body_type_ids[]', bodyType);
      });
    }

    // Add color cFilters (multiple values)
    if (newFilters.colors?.length > 0) {
      newFilters.colors.forEach(color => {
        params.append('color_ids[]', color);
      });
    }

    // Always include these params
    params.set('per_page', itemsPerPage.toString());
    params.set('page', page.toString());
    params.set('lang', 'en');
    params.set('currency', 'usd');

    const queryString = params.toString();
    router(`/cars${queryString ? `?${queryString}` : ''}`, { replace: false });
  }, [router, itemsPerPage]);

  // Fetch cars from API
  const fetchCars = useCallback(async (cFilters, searchTerm, sortBy, page = 1) => {
    setLoading(true);
    try {
      const params = {
        lang: 'en',
        per_page: itemsPerPage,
        page: page,
        currency: 'usd',
      };

      // Add brand cFilters (multiple values)
      if (cFilters.brands?.length > 0) {
        cFilters.brands.forEach((brand, index) => {
          params[`brand_ids[${index}]`] = brand;
        });
      }

      /* // Add brand cFilters (multiple values)
      if (cFilters.brands?.length > 0) {
        params.brand_ids = cFilters.brands;
      } */

      // Add fuel type (single value)
      if (cFilters.fuelType) {
        params.fuel_type = cFilters.fuelType;
      }

      // Add transmission (single value)
      if (cFilters.transmission) {
        params.transmission_type = cFilters.transmission;
      }

      // Add body type cFilters (multiple values)
      if (cFilters.bodyTypes?.length > 0) {
        cFilters.bodyTypes.forEach((bodyType, index) => {
          params[`body_type_ids[${index}]`] = bodyType;
        });
      }

      // // Add color cFilters (multiple values)
      if (cFilters.colors?.length > 0) {
        cFilters.colors.forEach((color, index) => {
          params[`color_ids[${index}]`] = color;
        });
      }

      /* // Add body type cFilters (multiple values)
      if (cFilters.bodyTypes?.length > 0) {
        params.body_type_ids = cFilters.bodyTypes;
      }

      // Add color cFilters (multiple values)
      if (cFilters.colors?.length > 0) {
        params.color_ids = cFilters.colors;
      } */

      if (searchTerm) {
        params.search = searchTerm;
      }
      if (sortBy && sortBy !== 'name') {
        params.sort_by = sortBy;
      }

      //console.log('Fetching with params:', params);

      const carsData = await fetchFromAPI(API_ENDPOINTS.CAR_LIST, params);
      //console.log("response data ==========>", carsData);

      // Update state with pagination info from API response
      setAllCars(carsData.data || []);
      setTotalCars(carsData.total || 0);
      setTotalPages(carsData.last_page || 1);

    } catch (err) {
      console.error('Error fetching cars:', err);
      setError(err.message);
      setAllCars([]);
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage]);

  // Debounced fetch function
  const debouncedFetch = useCallback(
    debounce((cFilters, searchTerm, sortBy, page = 1) => {
      //console.log("called from debounce fetch");
      fetchCars(cFilters, searchTerm, sortBy, page);
      updateURL(cFilters, searchTerm, sortBy, page);
    }, 500),
    [fetchCars, updateURL]
  );

  // Handle filter selection for checkboxes (brands, colors, bodyTypes)
  const handleFilterChange = (filterType, value, isChecked) => {
    const newFilters = { ...selectedFilters };

    if (filterType === 'brands' || filterType === 'colors' || filterType === 'bodyTypes') {
      const currentFilters = [...(newFilters[filterType] || [])];

      if (isChecked) {
        if (!currentFilters.includes(value)) {
          currentFilters.push(value);
        }
      } else {
        const index = currentFilters.indexOf(value);
        if (index > -1) {
          currentFilters.splice(index, 1);
        }
      }
      newFilters[filterType] = currentFilters;
    } else {
      // For radio buttons (transmission, fuelType)
      newFilters[filterType] = isChecked ? value : '';
    }

    setSelectedFilters(newFilters);
    setCurrentPage(1);
    //console.log("called debounce fetch from handle filter change");
    debouncedFetch(newFilters, searchTerm, sortBy, 1);
    updateURL(newFilters, searchTerm, sortBy, 1);
  };

  // Handle search term change
  const handleSearchChange = (newSearchTerm) => {
    setSearchTerm(newSearchTerm);
    setCurrentPage(1); // Reset to first page when search changes
    //console.log("called debounce fetch from handle search change");
    debouncedFetch(selectedFilters, newSearchTerm, sortBy, 1);
  };

  // Handle sort change
  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
    setCurrentPage(1); // Reset to first page when sort changes
    //console.log("called from handle sort change");
    fetchCars(selectedFilters, searchTerm, newSortBy, 1);
    updateURL(selectedFilters, searchTerm, newSortBy, 1);
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      //console.log("called from handle page change");
      fetchCars(selectedFilters, searchTerm, sortBy, newPage);

      updateURL(selectedFilters, searchTerm, sortBy, newPage);
      // Scroll to top when page changes
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Clear all cFilters
  const clearAllFilters = () => {
    const clearedFilters = {
      bodyTypes: [],
      transmission: '',
      brands: [],
      colors: [],
      fuelType: ''
    };
    setSelectedFilters(clearedFilters);
    setSearchTerm('');
    setSortBy('name');
    setCurrentPage(1);

    //console.log('called from clear all filters');

    // Fetch cars with cleared cFilters
    fetchCars(clearedFilters, '', 'name', 1);
    updateURL(clearedFilters, '', 'name', 1);
  };

  const FilterContent = () => (
    <div className="space-y-4">
      <h2 className="text-white font-semibold text-lg pb-10">{t('list.filters')}</h2>

      {/* Filter Section - Body Type (Multiple Selection) */}
      {cFilters?.bodyTypes?.length > 0 && (
        <div className="border-b border-[#FFFFFF] pb-4">
          <button
            className="flex justify-between items-center w-full text-gray-300 hover:text-white"
            onClick={() => setShowBodyType(!showBodyType)}
          >
            <span>{t('details.body_type')}</span>
            {showBodyType ? <ChevronDown size={16} /> : <ChevronRightFilter size={16} />}
          </button>

          {showBodyType && (
            <div className="mt-2 space-y-2 text-sm text-gray-400">
              {cFilters.bodyTypes.map((type) => (
                <div key={type.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="accent-blue-500 ml-5"
                    checked={(selectedFilters.bodyTypes || []).includes(type.id)}
                    onChange={(e) => handleFilterChange('bodyTypes', type.id, e.target.checked)}
                  />
                  <label>{type.name}</label>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Brands (Multiple Selection) */}
      {cFilters?.brands?.length > 0 && (
        <div className="border-b border-[#FFFFFF] pb-4">
          <button
            className="flex justify-between items-center w-full text-gray-300 hover:text-white"
            onClick={() => setShowBrands(!showBrands)}
          >
            <span>{t('extra.brands')}</span>
            {showBrands ? <ChevronDown size={16} /> : <ChevronRightFilter size={16} />}
          </button>
          {showBrands && (
            <div className="mt-2 space-y-2 text-sm text-gray-400">
              {cFilters.brands.map((brand) => (
                <div key={brand.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="accent-blue-500 ml-5"
                    checked={(selectedFilters.brands || []).includes(brand.id)}
                    onChange={(e) => handleFilterChange('brands', brand.id, e.target.checked)}
                  />
                  <label>{brand.name}</label>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Colors (Multiple Selection) */}
      {cFilters?.colors?.length > 0 && (
        <div className="border-b border-[#FFFFFF] pb-4">
          <button
            className="flex justify-between items-center w-full text-gray-300 hover:text-white"
            onClick={() => setShowColor(!showColor)}
          >
            <span>{t('details.color')}</span>
            {showColor ? <ChevronDown size={16} /> : <ChevronRightFilter size={16} />}
          </button>
          {showColor && (
            <div className="mt-2 space-y-2 text-sm text-gray-400">
              {cFilters.colors.map((item) => (
                <div key={item.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="accent-blue-500 ml-5"
                    checked={(selectedFilters.colors || []).includes(item.id)}
                    onChange={(e) => handleFilterChange('colors', item.id, e.target.checked)}
                  />
                  <label>{item.name}</label>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Fuel Type (Radio Selection) */}
      {cFilters?.fuelTypes?.length > 0 && (
        <div className="border-b border-[#FFFFFF] pb-4">
          <button
            className="flex justify-between items-center w-full text-gray-300 hover:text-white"
            onClick={() => setShowFuel(!showFuel)}
          >
            <span>{t('details.fuel_type')}</span>
            {showFuel ? <ChevronDown size={16} /> : <ChevronRightFilter size={16} />}
          </button>
          {showFuel && (
            <div className="mt-2 space-y-2 text-sm text-gray-400">
              {cFilters.fuelTypes.map((item) => (
                <div key={item.id} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="fuelType"
                    className="accent-blue-500 ml-5"
                    checked={selectedFilters.fuelType === item.id}
                    onChange={(e) => handleFilterChange('fuelType', item.id, e.target.checked)}
                  />
                  <label>{item.name === "Petrol" ?
                    t('extra.filter.fuelType.petrol')
                    : item.name === "Diesel" ? t('extra.filter.fuelType.diesel') : item.name === "Electric" ?
                      t('extra.filter.fuelType.charge') : t('extra.filter.fuelType.other')}</label>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Transmission Type (Radio Selection) */}
      {cFilters?.transmissionTypes?.length > 0 && (
        <div className="border-b border-[#FFFFFF] pb-4">
          <button
            className="flex justify-between items-center w-full text-gray-300 hover:text-white"
            onClick={() => setShowTransmission(!showTransmission)}
          >
            <span>{t('details.transmission')}</span>
            {showTransmission ? <ChevronDown size={16} /> : <ChevronRightFilter size={16} />}
          </button>
          {showTransmission && (
            <div className="mt-2 space-y-2 text-sm text-gray-400">
              {cFilters.transmissionTypes.map((item) => (
                <div key={item.id} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="transmission"
                    className="accent-blue-500 ml-5"
                    checked={selectedFilters.transmission === item.id}
                    onChange={(e) => handleFilterChange('transmission', item.id, e.target.checked)}
                  />
                  <label>{item.name === "Manual" ? t('extra.filter.transmission.manual') : item.name === "Automatic" ? t('extra.filter.transmission.automatic') : t('extra.filter.transmission.other')}</label>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-[var(--background)] text-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">Error loading cars: {error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--background)] text-white pl-4 sm:px-6 md:px-8 lg:pl-12 xl:px-[120px] py-3 pt-[80px]">
      {/* Header */}
      <div className="bg-[var(--background)] shadow-sm">
        <div className="container mx-auto px-4 py-6 lg:py-8">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-9">
            {t('list.title')}
          </h1>

          {/* Popular Cars Slider Section */}
          {popularCarsData && popularCarsData.length > 0 && (
            <div className="mb-8">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg text-white font-bold">{t('list.popular')}</h2>

                {/* Navigation Buttons */}
                {popularCarsData.length > slidesToShow && (
                  <div className="flex gap-2">
                    <button
                      onClick={prevSlide}
                      className="p-2 rounded-full bg-gray-800 hover:bg-gray-700 text-white transition-colors"
                      aria-label="Previous slide"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button
                      onClick={nextSlide}
                      className="p-2 rounded-full bg-gray-800 hover:bg-gray-700 text-white transition-colors"
                      aria-label="Next slide"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                )}
              </div>

              {/* Slider Container */}
              <div className="relative overflow-hidden">
                <div
                  className="flex transition-transform duration-500 ease-in-out"
                  style={{
                    // Move by the width of one item per slide
                    transform: `translateX(-${currentSlide * (100 / slidesToShow)}%)`,
                  }}
                >
                  {popularCarsData.map((car, index) => (
                    <div
                      key={car.id}
                      className="flex-shrink-0"
                      style={{
                        // Each item takes up the percentage based on slides to show
                        width: `${100 / slidesToShow}%`,
                        paddingRight: '1rem' // gap between items
                      }}
                    >
                      <div
                        className="relative h-[240px] rounded-2xl overflow-hidden cursor-pointer 
             transition-transform duration-300 hover:scale-105"
                        style={{
                          backgroundImage: `url(${car.thumbnail})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                        }}
                        onClick={() => window.location.href = `/cars/${car.slug}`}
                      >
                        {/* TOP opacity layer */}
                        <div className="absolute top-0 left-0 w-full h-14 z-0" style={{background: '#00000047'}}/>

                        {/* BOTTOM opacity layer */}
                        <div className="absolute bottom-0 left-0 w-full h-14 z-0" style={{background: '#00000047'}}/>

                        {/* TOP CONTENT */}
                        <div className="relative z-10 flex justify-between items-start p-4">
                          <h3 className="text-white font-semibold text-sm sm:text-base max-w-[70%] leading-tight">
                            {car.name}
                          </h3>

                          <span className="bg-white text-black text-xs px-3 py-0.5 rounded-full" style={{color:'black'}}>
                            {car.available === "available"
                              ? t('extra.available')
                              : t('extra.booked')}
                          </span>
                        </div>

                        {/* BOTTOM CONTENT */}
                        <div className="absolute bottom-2 left-0 w-full px-4 flex justify-between items-center z-10">
                          {/* Brand */}
                          <img
                            src={car.brandLogo}
                            alt="Brand"
                            className="w-12 h-12 object-contain"
                          />

                          {/* Price */}
                          <span className="text-white font-bold text-sm sm:text-base">
                            {selectedCurrency.symbol}
                            {selectedCurrency.label === 'USD'
                              ? car.pricePerDayUsd
                              : car.pricePerDayAed}
                            <span className="text-xs font-light ml-1">
                              {t('list.perDay')}
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Slide Indicators */}
              {popularCarsData.length > slidesToShow && (
                <div className="flex justify-center mt-4 gap-2">
                  {Array.from({ length: totalPages }).map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index * slidesToShow)} // jump to page
                      className={`w-2 h-2 rounded-full transition-colors ${currentSlide >= index * slidesToShow &&
                        currentSlide < (index + 1) * slidesToShow
                        ? 'bg-blue-500'
                        : 'bg-gray-600'
                        }`}
                      aria-label={`Go to page ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Mobile Search Bar */}
      <div className="lg:hidden container mx-auto px-4 py-4">
        <div className="relative mb-4">
          <input
            type="text"
            placeholder="Search cars..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full bg-transparent border border-[#FFFFFF] text-white rounded-lg py-3 pl-4 pr-10 focus:outline-none focus:ring-2 focus:ring-[#FFFFFF]"
          />
          <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          {loading && (
            <div className="absolute right-10 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          )}
        </div>

        {/* Mobile Filter and Sort Bar */}
        <div className="flex gap-3 mb-4">
          <button
            onClick={() => setShowMobileFilters(true)}
            className="flex items-center gap-2 bg-transparent border border-[#FFFFFF] rounded-lg px-4 py-2 text-white font-light"
          >
            <Filter className="w-4 h-4" />
            {t('list.filters')}
            {/* Show active filter count */}

          </button>
          <select
            value={sortBy}
            onChange={(e) => handleSortChange(e.target.value)}
            className="flex-1 bg-transparent border border-[#FFFFFF] rounded-lg px-3 py-2 text-white text-sm appearance-none"
          >
            <option className="text-black" value="">Sort by</option>
            <option className="text-black" value="lowest_price">Price: Low to High</option>
            <option className="text-black" value="highest_price">Price: High to Low</option>
          </select>
        </div>
      </div>

      {/* Mobile Filter Modal */}
      {showMobileFilters && (
        <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-50">
          <div className="absolute bottom-0 left-0 right-0 bg-gray-900 rounded-t-2xl max-h-[80vh] overflow-y-auto">
            <div className="p-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-white">{t('list.filters')}</h2>
                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <FilterContent />
              <div className="flex gap-3 mt-6 pt-4 border-t border-[#FFFFFF]">
                <button
                  onClick={clearAllFilters}
                  className="flex-1 bg-gray-700 text-white py-3 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  {t('list.clear')}
                </button>
                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('list.apply')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Container */}
      <div className="container mx-auto px-4 pb-8">
        <div className="flex gap-6">
          {/* Desktop Sidebar Filters */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            {/* Desktop Search */}
            <div className="mb-6 relative">
              <input
                type="text"
                placeholder={t('list.search')}
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full bg-transparent border border-[#FFFFFF] text-white rounded-lg py-3 pl-4 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              {loading && (
                <div className="absolute right-10 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              )}
            </div>

            {/* Filters Card */}
            <div className="border border-[#FFFFFF] bg-transparent rounded-lg p-4">
              <FilterContent />
              <button
                onClick={clearAllFilters}
                className="w-full mt-4 bg-gray-700 text-white py-2 rounded-lg hover:bg-gray-600 transition-colors"
              >
                {t('list.clear')}
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Desktop Top Bar */}
            <div className="hidden lg:flex justify-between items-center mb-6">
              <span className="text-gray-300 text-sm font-bold">
                {t('list.available')} ({totalCars})
                {loading && <span className="ml-2 text-blue-400">{t('extra.updating')}</span>}
              </span>
              <select
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value)}
                className="bg-transparent border border-[#FFFFFF] rounded-lg px-3 py-3 text-white text-sm appearance-none"
              >
                <option className="text-black" value="">{t('list.sortBy')}</option>
                <option className="text-black" value="lowest_price">Price: Low to High</option>
                <option className="text-black" value="highest_price">Price: High to Low</option>
              </select>
            </div>

            {/* Cars Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 lg:gap-6">
              {allCars.map((car, index) => (
                <CarComponent key={car.id || index} car={car} />
              ))}
            </div>

            {/* Loading indicator */}
            {loading && (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                <span className="ml-2 text-blue-400">{t('extra.updating')}</span>
              </div>
            )}

            {/* Pagination */}
            {totalCars > 0 && totalPages > 1 && (
              <div className="flex flex-col sm:flex-row justify-center items-center mt-8 gap-4">
                <div className="text-white text-lg font-medium">
                  {t('list.Total')}: {totalCars}
                </div>

                <div className="flex gap-2 sm:gap-4 flex-wrap justify-center">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className={`
                      flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-colors text-sm sm:text-base
                      ${currentPage <= 1
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-black hover:bg-gray-100'
                      }
                    `}
                  >
                    {t('list.previous')}
                  </button>

                  {/* Page numbers - Show only 3 pages */}
                  <div className="flex items-center gap-2">
                    {/* Show first page if not in view */}
                    {currentPage > 2 && (
                      <>
                        <button
                          onClick={() => handlePageChange(1)}
                          className="px-3 py-2 rounded-lg bg-gray-700 text-white hover:bg-gray-600 text-sm sm:text-base"
                        >
                          1
                        </button>
                        {currentPage > 3 && <span className="text-gray-400">...</span>}
                      </>
                    )}

                    {/* Show 3 pages around current page */}
                    {(() => {
                      let startPage, endPage;

                      if (currentPage === 1) {
                        // At the beginning
                        startPage = 1;
                        endPage = Math.min(3, totalPages);
                      } else if (currentPage === totalPages) {
                        // At the end
                        startPage = Math.max(1, totalPages - 2);
                        endPage = totalPages;
                      } else {
                        // In the middle
                        startPage = currentPage - 1;
                        endPage = currentPage + 1;

                        // Adjust if we're near the beginning
                        if (startPage < 1) {
                          startPage = 1;
                          endPage = Math.min(3, totalPages);
                        }

                        // Adjust if we're near the end
                        if (endPage > totalPages) {
                          endPage = totalPages;
                          startPage = Math.max(1, totalPages - 2);
                        }
                      }

                      return Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                        const pageNum = startPage + i;
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`
                              px-3 py-2 rounded-lg font-medium transition-colors text-sm sm:text-base
                              ${pageNum === currentPage
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-700 text-white hover:bg-gray-600'
                              }
                            `}
                          >
                            {pageNum}
                          </button>
                        );
                      });
                    })()}

                    {/* Show last page if not in view */}
                    {currentPage < totalPages - 1 && (
                      <>
                        {currentPage < totalPages - 2 && <span className="text-gray-400">...</span>}
                        <button
                          onClick={() => handlePageChange(totalPages)}
                          className="px-3 py-2 rounded-lg bg-gray-700 text-white hover:bg-gray-600 text-sm sm:text-base"
                        >
                          {totalPages}
                        </button>
                      </>
                    )}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className={`
                      flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-colors text-sm sm:text-base
                      ${currentPage >= totalPages
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-black hover:bg-gray-100'
                      }
                    `}
                  >
                    {t('list.next')}
                  </button>
                </div>

                {/* Page info */}
                <div className="text-gray-400 text-sm">
                  Page {currentPage} of {totalPages}
                </div>
              </div>
            )}

            {/* No Results */}
            {allCars.length === 0 && !loading && (
              <div className="text-center py-12">
                <Car className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No vehicles found</h3>
                <p className="text-gray-400">Try adjusting your search criteria or Filters</p>
                <button
                  onClick={clearAllFilters}
                  className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Clear All Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}